import { Banner, BookDemoWorker, CalensoFeature, Customer, Reference, Statistics, Usp } from '@models/app.models';

const BANNER: Record<string, Banner> = {
  calenso: {
    title: '#1 Enterprise Online-Terminvereinbarungs Lösung',
    description:
      'Die Customer Engagment Platform von Calenso integriert die Terminplanung mit Videoberatung, Gruppenterminen, Raumbuchung und detaillierten Analysen',
  },
};

const COMPANIES: Record<string, Customer> = {
  vivawest: {
    name: 'Vivawest',
    logo: 'assets/customer-logos/vivawest.png',
    identifier: 'vivawest',
  },
  nkb: {
    name: 'NKB',
    logo: 'assets/customer-logos/nkb.png',
    identifier: 'nkb',
  },
  richner: {
    name: 'Richner',
    logo: 'assets/customer-logos/richner.png',
    identifier: 'bms',
  },
  terminbuchung: {
    name: 'Terminbuchung Universität Zürich',
    logo: 'assets/customer-logos/uszh.webp',
    identifier: 'usz',
  },
  sanitas: {
    name: 'Sanitas',
    logo: 'assets/customer-logos/sanitas.webp',
    identifier: 'sanitas',
  },
  helsana: {
    name: 'Helsana',
    logo: 'assets/customer-logos/helsana.svg',
    identifier: 'helsana',
  },
  dextra: {
    name: 'Dextra',
    logo: 'assets/customer-logos/dextra.svg',
    identifier: 'dextra',
  },
  helvetia: {
    name: 'Helvetia',
    logo: 'assets/customer-logos/helvetia.webp',
    identifier: 'helvetia',
  },
  css: {
    name: 'CSS',
    logo: 'assets/customer-logos/css.webp',
    identifier: 'css',
  },
  terminplaner: {
    name: 'Terminplaner Kanton Luzern',
    logo: 'assets/customer-logos/ktlu.png',
    identifier: 'ktlu',
  },
  toyota: {
    name: 'Toyota',
    logo: 'assets/customer-logos/toyota.png',
    identifier: 'toyota',
  },
  gag: {
    name: 'GAG',
    logo: 'assets/customer-logos/gag.webp',
    identifier: 'gag'
  },
  wincasa: {
    name: 'Wincasa',
    logo: 'assets/customer-logos/wincasa.webp',
    identifier: 'wincasa'
  },
  white: {
    name: 'White immobilien',
    logo: 'assets/customer-logos/white.svg',
    identifier: 'white'
  },
  'lenze-friedman': {
    name: 'Immobilien Lenze',
    logo: 'assets/customer-logos/lenze-friedman.webp',
    identifier: 'lenze-friedman'
  },
  meravis: {
    name: 'Meravis',
    logo: 'assets/customer-logos/meravis.webp',
    identifier: 'meravis'
  },
  'immobilien-schoene': {
    name: 'Immobilien Schöne GmbH',
    logo: 'assets/customer-logos/immobilien-schoene.webp',
    identifier: 'immobilien-schoene'
  },
};

const FEATURES: CalensoFeature[] = [
  {
    identifier: 'online-booking',
    title: 'Online-Terminbuchung',
    description:
      'Bieten Sie Termine automatisiert und rund um die Uhr online an. Reduzieren Sie den Aufwand und erhöhen Sie die Kundenzufriedenheit.',
    icon: 'assets/icons/calendar.svg',
    image: 'assets/features/calenso-compact-bookingpage.webp',
  },
  {
    identifier: 'room-booking',
    title: 'Raumbuchung',
    description:
      'Buchen Sie Räume und Ressourcen automatisch zu Ihren Terminen dazu. Doppelbuchungen gehören der Vergangenheit an.',
    icon: 'assets/icons/meeting-room.svg',
    image: 'assets/features/calendar-integrations-online-scheduling-calenso.webp',
  },
  {
    identifier: 'surveys-configurators',
    title: 'Umfragen & Konfiguratoren',
    description:
      'Erheben Sie vor dem Termin alle nötigen Informationen zur Qualifizierung. So sind Sie optimal vorbereitet und sparen Zeit im Gespräch.',
    icon: 'assets/icons/checklist.svg',
    image: 'assets/features/calenso-terminplanung-crm-integrationen.webp',
  },
  {
    identifier: 'automated-messages',
    title: 'Automatisierte Nachrichten',
    description:
      'Versenden Sie automatisierte E-Mail oder SMS Erinnerungen an Ihre Kandidaten, um sie an einen Termin zu erinnern, sich zu bedanken oder um nach einem neuen Termin zu fragen.',
    icon: 'assets/icons/email.svg',
    image: 'assets/features/sms-nachrichten-terminerinnerung-calenso.webp',
  },
  {
    identifier: 'video-voice-meetings',
    title: 'Video- & Voicemeetings',
    description:
      'Beraten Sie Ihre Kunden ortsunabhängig via Zoom, MS Teams oder Google Meet. Die Einladungen werden automatisch verschickt.',
    icon: 'assets/icons/video-camera.svg',
    image: 'assets/features/qualifizierungsfragen-leadqualifying-scheduling.webp',
  },
  {
    identifier: 'analytics-tracking',
    title: 'Analysen & Tracking',
    description:
      'Behalten Sie Ihre wichtigsten KPIs im Blick. Treffen Sie datenbasierte Entscheidungen zur Optimierung Ihrer Prozesse.',
    icon: 'assets/icons/pie-chart.svg',
    image: 'assets/features/schnelle-ladezeit-calenso-uptime.webp',
  },
];

const USPS: Record<string, Usp> = {
  security: {
    identifier: 'security',
    title: 'Schweizer Sicherheit & Datenschutz',
    description:
      'Bei Calenso haben Datenschutz und Informationssicherheit die allerhöchste Priorität – sie bilden das Fundament unseres Handelns und gewährleisten den Schutz Ihrer sensiblen Daten auf höchstem Niveau. Wir setzen auf zertifizierte Standards, um Transparenz, Vertraulichkeit und Integrität zu sichern. Calenso ist <b>ISO 27001 zertifiziert</b> und vollständig <b>nDSG- sowie DSGVO-konform</b>.',
    image: 'assets/usps/calenso-onlinescheduling-analytics-blue.webp',
    features: [
      'Unsere Rechenzentren in der Schweiz sind nach <b>ISO 27001</b> und <b>ISO 9001</b> zertifiziert',
      'BSI C5 zertifizierte Web-Application-Firewall (WAF)',
      'AES-256 Verschlüsselung der Daten',
    ],
    reverse: false,
  },
  customizing: {
    identifier: 'customizing',
    title: 'Vollständige Anpassung an CI/CD',
    description:
      'Gestalten Sie Ihre Terminvereinbarung individuell nach Ihrem Branding. Ob Text, Farben, Schriftarten, Buttons, Schattierungen – einfach alles – schnell und vollständig anpassbar.',
    image: 'assets/usps/calenso-scheduling-widget-blue.webp',
    features: ['Eigenes Logo & Farben', 'Anpassbare Texte', 'Eigener Absender'],
    reverse: true,
  },
  support: {
    identifier: 'support',
    title: 'Erstklassiger & mehrsprachiger Support',
    description:
      'Unser Customer-Success-Team betreut Kunden persönlich, kompetent und mit tiefem Branchenverständnis. Von der Einführung bis zum laufenden Betrieb – wir sorgen für einen reibungslosen Ablauf und maximale Wirkung Ihrer Terminlösung. Mehrsprachig, effizient und immer erreichbar.',
    image: 'assets/usps/customizable-online-scheduling-form.webp',
    features: ['Persönlicher Account-Manager', 'Individueller Datenschutzvertrag (ADV)', 'Service Level Agreement'],
    reverse: false,
  },
};

const STATISTICS: Record<string, Statistics> = {
  basel: {
    identifier: 'basel',
    value: '250%',
    title: 'Zeitersparnis im Terminprozess',
    description:
      'Das ist die erste Lösung, mit welcher wir die effektiven Bedürfnisse von unseren Kunden abdecken können. Lange gesucht, endlich gefunden!',
    image: 'assets/customer-logos/ktbl.webp',
  },
  helvetia: {
    identifier: 'helvetia',
    value: '110%',
    title: 'Schnellere Terminfindung',
    description:
      'Seit wir Calenso nutzen, können Berater mehr Termine am Tag durchführen und haben eine bessere Übersicht über Klienten.',
    image: 'assets/customer-logos/helvetia.webp',
  },
  amag: {
    identifier: 'amag',
    value: '96%',
    title: 'Reduktion verpasster Termine',
    description:
      'Wir sind sehr froh, dass wir Calenso gefunden haben, denn es hat wirklich alle Funktionen, die wir brauchen.',
    image: 'assets/customer-logos/amag.webp',
  },
};

const REFERENCE_STORIES: Record<string, Reference> = {
  helvetia: {
    identifier: 'helvetia',
    logo: 'assets/customer-logos/helvetia.webp',
    title: 'Schnell und einfach einen Versicherungstermin vereinbaren',
    shortDescription:
      'Seit wir Calenso nutzen, können Berater mehr Termine am Tag durchführen, haben eine bessere Übersicht über Klienten und Informationen werden schon vor dem Termin automatisiert in unser CRM-System eingetragen.',
    fullDescription:
      'Seit wir Calenso nutzen, können Berater mehr Termine am Tag durchführen, haben eine bessere Übersicht über Klienten und Informationen werden schon vor dem Termin automatisiert in unser CRM-System eingetragen.',
    companySize: '12’000 Mitarbeiter',
    industry: 'Versicherung',
    customerSince: 'September 2020',
    personName: 'Maik Fettig',
    personRole: 'Strategie und Innovations Manager',
    personImage: 'assets/images/maik-fettig.png',
    detailedView: false,
  },
  css: {
    identifier: 'css',
    logo: 'assets/customer-logos/css.webp',
    title: 'Ausgezeichnete Zusammenarbeit',
    shortDescription:
      'Die Arbeit mit Calenso funktioniert reibungslos. Wir sind vor allem vom reaktiven und kompetenten Kundendienst sehr begeistert. Wir können Calenso wärmstens weiterempfehlen.',
    fullDescription:
      'Die Arbeit mit Calenso funktioniert reibungslos. Wir sind vor allem vom reaktiven und kompetenten Kundendienst sehr begeistert. Wir können Calenso wärmstens weiterempfehlen.',
    companySize: '3’000 Mitarbeiter',
    industry: 'Versicherung',
    customerSince: 'Juni 2024',
    personName: 'Anne Bode',
    personRole: 'Abteilungsleiterin Gesundheitsmanagement',
    personImage: 'assets/images/anne-bode.png',
    detailedView: false,
  },
  sanitas: {
    identifier: 'sanitas',
    logo: 'assets/customer-logos/sanitas.webp',
    title: 'Mehr Termine mit Digitalisierungs-Suite',
    shortDescription:
      'Die Terminbuchungen bei unseren Versicherungsberater:innen erhöhen sich fortlaufend und wir schätzen die unkomplizierte und partnerschaftliche Zusammenarbeit zwischen Calenso und Sanitas.',
    fullDescription:
      'Die Terminbuchungen bei unseren Versicherungsberater:innen erhöhen sich fortlaufend und wir schätzen die unkomplizierte und partnerschaftliche Zusammenarbeit zwischen Calenso und Sanitas.',
    companySize: '760 Mitarbeiter',
    industry: 'Versicherung',
    customerSince: 'Januar 2021',
    personName: 'Ramez El Chami',
    personRole: 'Project Manager Tactical Sales',
    personImage: 'assets/images/ramez-ei-chami.png',
    detailedView: false,
  },
  groupemutuel: {
    identifier: 'groupemutuel',
    logo: 'assets/customer-logos/groupe-mutuel.webp',
    title: 'Effiziente Triage von Kundenterminen',
    shortDescription:
      'Mit Calenso haben wir eine Schweizer Firma gefunden, die auch unsere landestypischen Eigenheiten abdecken kann. Die Zusammenarbeit macht richtig Spass.',
    fullDescription:
      'Mit Calenso haben wir eine Schweizer Firma gefunden, die auch unsere landestypischen Eigenheiten abdecken kann. Die Zusammenarbeit macht richtig Spass.',
    companySize: '2’800 Mitarbeiter',
    industry: 'Versicherung',
    customerSince: 'Juli 2020',
    personName: 'Pasquale D’Agostino',
    personRole: 'Projektleiter',
    personImage: 'assets/images/pasquale_dagostino.jpg',
    detailedView: false,
  },
  proinfirmis: {
    identifier: 'proinfirmis',
    logo: 'assets/customer-logos/proinfirmis.webp',
    title: 'Pro Infirmis setzt auf barrierefreie Terminvereinbarung',
    shortDescription:
      'Eine flexible und barrierefreie Terminvereinbarung ist essenziell, um allen Menschen den Zugang zu unseren Beratungsangeboten zu erleichtern. Die digitale Lösung von Calenso ermöglicht genau das – effizient, intuitiv und unabhängig von Öffnungszeiten.',
    fullDescription:
      'Eine flexible und barrierefreie Terminvereinbarung ist essenziell, um allen Menschen den Zugang zu unseren Beratungsangeboten zu erleichtern. Die digitale Lösung von Calenso ermöglicht genau das – effizient, intuitiv und unabhängig von Öffnungszeiten.',
    companySize: '800 Mitarbeiter',
    industry: 'Org. für Menschen mit Behinderungen',
    customerSince: 'September 2020',
    personName: 'Beatrice Schwaiger',
    personRole: 'Leiterin Beratung',
    personImage: 'assets/images/beatrice_schwaiger.jpg',
    detailedView: false,
  },
  waswira: {
    identifier: 'waswira',
    logo: 'assets/customer-logos/waswira.png',
    title: 'Effiziente Terminvereinbarung für die Arbeitslosenkasse',
    shortDescription:
      'Unser Ziel ist es, Personen Zugang zu unseren Beratungsleistungen so einfach wie möglich zu machen. Die digitale Terminvereinbarung von Calenso hat uns sehr viel Arbeit abgenommen und hat auch die Zuteilung der STES zu den Personalberatenden sehr vereinfacht.',
    fullDescription:
      'Unser Ziel ist es, Personen Zugang zu unseren Beratungsleistungen so einfach wie möglich zu machen. Die digitale Terminvereinbarung von Calenso hat uns sehr viel Arbeit abgenommen und hat auch die Zuteilung der STES zu den Personalberatenden sehr vereinfacht.',
    companySize: '500 Mitarbeiter',
    industry: 'Arbeitslosenkasse',
    customerSince: 'Mai 2025',
    personName: 'Stefanie Steiner',
    personRole: 'Leiterin Support RAV & Projektleitung RAV-Koordination',
    personImage: 'assets/images/stefanie-steiner.png',
    detailedView: false,
  },
  regensdorf: {
    identifier: 'regensdorf',
    logo: 'assets/customer-logos/regensdorf.svg',
    title: 'Tradition und Innovation vereinen',
    shortDescription:
      'Die Unterstützung durch den Customer Success war massgeblich für den Erfolg der Implementierung verantwortlich, da sie auf die spezifischen Anforderungen der Gemeinde eingingen und alle Wünsche zur Zufriedenheit umsetzten.',
    fullDescription:
      'Die Unterstützung durch den Customer Success war massgeblich für den Erfolg der Implementierung verantwortlich, da sie auf die spezifischen Anforderungen der Gemeinde eingingen und alle Wünsche zur Zufriedenheit umsetzten.',
    companySize: '20’000 Einwohner',
    industry: 'Behörde / Verwaltung',
    customerSince: 'Februar 2024',
    personName: 'Debora Isufi',
    personRole: 'Direktionsassistentin',
    personImage: 'assets/images/debora-isufi.png',
    detailedView: false,
  },
  ktbs: {
    identifier: 'ktbs',
    logo: 'assets/customer-logos/ktbs.png',
    title: 'Terminvereinbarung per Knopfdruck',
    shortDescription:
      'Die Zusammenarbeit mit Calenso war professionell und zielgerichtet. Unsere Bedürfnisse wurden verstanden und effizient umgesetzt.',
    fullDescription:
      'Die Zusammenarbeit mit Calenso war professionell und zielgerichtet. Unsere Bedürfnisse wurden verstanden und effizient umgesetzt.',
    companySize: '6’000 Mitarbeitenden',
    industry: 'Behörde',
    customerSince: 'Januar 2022',
    personName: 'Maia Henz',
    personRole: 'Projektleiter Justiz- und Sicherheitsdepartement',
    personImage: 'assets/images/maia_henz.jpg',
    detailedView: false,
  },
  kantonbaselstadt: {
    identifier: 'kantonbaselstadt',
    logo: 'assets/customer-logos/ktbs.png',
    title: 'Sicherheit und Anpassungsfähigkeit',
    shortDescription:
      'Die Zusammenarbeit mit Calenso war professionell und zielgerichtet. Unsere Bedürfnisse wurden verstanden und effizient umgesetzt.',
    fullDescription:
      'Die Zusammenarbeit mit Calenso war professionell und zielgerichtet. Unsere Bedürfnisse wurden verstanden und effizient umgesetzt.',
    companySize: '11’900 Mitarbeitenden',
    industry: 'Behörde / Verwaltung',
    customerSince: 'Januar 2021',
    personName: 'Roman Mathis',
    personRole: 'Projektleiter Justiz- und Sicherheitsdepartement',
    personImage: 'assets/images/roman-mathis.png',
    detailedView: false,
  },
  sge: {
    identifier: 'sge',
    logo: 'assets/customer-logos/sge.png',
    title: 'S-GE spart mehrere Stunden pro Woche mit Online-Terminvereinbarung',
    shortDescription:
      'Nur von Montag bis Freitag während der Geschäftszeiten einen Termin telefonisch zu buchen, ist einfach old-school!',
    fullDescription:
      'Nur von Montag bis Freitag während der Geschäftszeiten einen Termin telefonisch zu buchen, ist einfach old-school!',
    companySize: '200 Mitarbeitenden',
    industry: 'Beratung',
    customerSince: 'Januar 2018',
    personName: 'Heidi Meiller-Jester',
    personRole: 'Projektmanager Strategisches Marketing',
    personImage: 'assets/images/heidi-meiller-jester.png',
    detailedView: false,
  },
  nkb: {
    identifier: 'nkb',
    logo: 'assets/customer-logos/nkb.png',
    title: 'Beratungstermine bequem online vereinbaren bei der Nidwaldner Kantonalbank',
    shortDescription:
      'Unsere Kundinnen und Kunden können nun unabhängig von Zeit und Gerät Beratungstermine zu ihrem Wunschthema buchen. Die online eingegebenen Daten ermöglichen es unseren Beratenden, sich optimal auf die Gespräche vorzubereiten.',
    fullDescription:
      'Unsere Kundinnen und Kunden können nun unabhängig von Zeit und Gerät Beratungstermine zu ihrem Wunschthema buchen. Die online eingegebenen Daten ermöglichen es unseren Beratenden, sich optimal auf die Gespräche vorzubereiten.',
    companySize: '166 Mitarbeitenden',
    industry: 'Banken & Finanzdienstleister',
    customerSince: 'Dezember 2022',
    personName: 'Nicole Lüthy',
    personRole: 'Leiterin Private Kunden und Mitglied der Geschäftsleitung',
    personImage: 'assets/images/nicole-luethy.png',
    detailedView: false,
  },
  liberty: {
    identifier: 'liberty',
    logo: 'assets/customer-logos/liberty.png',
    title: 'Kunden der Liberty Vorsorge können neu Termine für ihre Finanzanliegen bequem online buchen.',
    shortDescription:
      'Mit Calenso konnten wir einen durchgängig digitalen Prozess für die Terminvereinbarung realisieren – ohne Medienbrüche und mit minimalem Integrationsaufwand. Die Lösung fügt sich technisch sauber in unsere Infrastruktur ein und ist ein echter Mehrwert für unsere IT-gestützte Kundeninteraktion.',
    fullDescription:
      'Mit Calenso konnten wir einen durchgängig digitalen Prozess für die Terminvereinbarung realisieren – ohne Medienbrüche und mit minimalem Integrationsaufwand. Die Lösung fügt sich technisch sauber in unsere Infrastruktur ein und ist ein echter Mehrwert für unsere IT-gestützte Kundeninteraktion.',
    companySize: '73 Mitarbeitenden',
    industry: 'Banken & Finanzdienstleister',
    customerSince: 'April 2024',
    personName: 'Daniel Hafner',
    personRole: 'Head Technology Innovation',
    personImage: 'assets/images/daniel_hafner.png',
    detailedView: false,
  },
  vivawest: {
    identifier: 'vivawest',
    logo: 'assets/customer-logos/vivawest.png',
    title: 'Neuartige Wege im Immobiliensektor',
    shortDescription:
      'Mit Calenso haben wir eine Lösung gefunden, die sich nahtlos in unsere Systeme wie SAP integriert und unsere Kundenkommunikation deutlich verbessert. Unsere Mieter:innen erhalten nun verbindliche Rückruftermine – und wir sparen intern wertvolle Zeit.',
    fullDescription:
      'Mit Calenso haben wir eine Lösung gefunden, die sich nahtlos in unsere Systeme wie SAP integriert und unsere Kundenkommunikation deutlich verbessert. Unsere Mieter:innen erhalten nun verbindliche Rückruftermine – und wir sparen intern wertvolle Zeit.',
    companySize: '2’000 Mitarbeitenden',
    industry: 'Immobilienhandel',
    customerSince: 'Oktober 2021',
    personName: 'Katharina Schwabe',
    personRole: 'Inhouse Consultant',
    personImage: 'assets/images/katharina_schwabe.png',
    detailedView: false,
  },
  bms: {
    identifier: 'bms',
    logo: 'assets/customer-logos/richner.png',
    title: 'BMS Building Materials Suisse – Der einzigartige Termin-Konfigurator',
    shortDescription:
      'Calenso bietet uns die einzigartige Möglichkeit Lead Generierung und digitale Terminvereinbarung miteinander zu verbinden. Mit den online Umfragen können sich unsere Berater perfekt auf die Beratungsgespräche vorbereiten.',
    fullDescription:
      'Calenso bietet uns die einzigartige Möglichkeit Lead Generierung und digitale Terminvereinbarung miteinander zu verbinden. Mit den online Umfragen können sich unsere Berater perfekt auf die Beratungsgespräche vorbereiten.',
    companySize: '1’800 Mitarbeitenden',
    industry: 'Einzelhandel',
    customerSince: 'Mai 2020',
    personName: 'Sam von Rotz',
    personRole: 'Leiter Ausstellungen D-CH',
    personImage: 'assets/images/sam-von-rotz.png',
    detailedView: false,
  },
  amag: {
    identifier: 'amag',
    logo: 'assets/customer-logos/amag.webp',
    title: 'Wie AMAG Online-Terminvereinbarung für ein innovatives Autoverkaufserlebnis nutzt',
    shortDescription:
      'Calenso bietet uns die einzigartige Möglichkeit Lead Generierung und digitale Terminvereinbarung miteinander zu verbinden. Mit den online Umfragen können sich unsere Berater perfekt auf die Beratungsgespräche vorbereiten.',
    fullDescription:
      'Calenso bietet uns die einzigartige Möglichkeit Lead Generierung und digitale Terminvereinbarung miteinander zu verbinden. Mit den online Umfragen können sich unsere Berater perfekt auf die Beratungsgespräche vorbereiten.',
    companySize: '6’500 Mitarbeitenden',
    industry: 'Automobilindustrie',
    customerSince: 'Juni 2021',
    personName: 'Peter Schmid',
    personRole: 'Head of new mobility hub',
    personImage: 'assets/images/peter-schmid.png',
    detailedView: false,
  },
  ochsner: {
    identifier: 'ochsner',
    logo: 'assets/customer-logos/ochsner_sport.png',
    title: 'In kurzer Zeit zum Laufanalyse-Termin',
    shortDescription:
      'Nach der Einführung der Online-Terminvereinbarung bei OCHSNER SPORT sind die Buchungen signifikant angestiegen.',
    fullDescription:
      'Nach der Einführung der Online-Terminvereinbarung bei OCHSNER SPORT sind die Buchungen signifikant angestiegen.',
    companySize: '1’600 Mitarbeitenden',
    industry: 'Einzelhandel',
    customerSince: 'Oktober 2021',
    personName: 'Deborah Lüthi',
    personRole: 'Head of Omnichannel',
    personImage: 'assets/images/ralf.webp',
    detailedView: false,
  },
};

const BOOK_DEMO: Record<string, BookDemoWorker> = {
  ralf: {
    name: 'Ralf',
    image: 'assets/images/ralf.webp',
    widgetLink: 'https://widget.calenso.com/?partner=calenso-meeting&isFrame=true&lang=de_CH&utm_source=outreach&utm_medium=email&type=appointment&store_id=15166&service[]=37798&category[]=11978&worker_id=66990&lang-switcher=false',
  },
  nina: {
    name: 'Nina',
    image: 'assets/images/nina.png',
    widgetLink: 'https://widget.calenso.com/?partner=calenso-meeting&isFrame=true&lang=de_CH&utm_source=outreach&utm_medium=email&type=appointment&store_id=15166&category[]=11978&service[]=42819&worker_id=80187&lang-switcher=false',
  },
  patrick: {
    name: 'Patrick',
    image: 'assets/images/patrick_k.png',
    widgetLink: 'https://widget.calenso.com/?partner=calenso-meeting&isFrame=true&lang=de_CH&utm_source=outreach&utm_medium=email&type=appointment&store_id=15166&worker_id=78306&service[]=42819&lang-switcher=false',
  },
  johanna: {
    name: 'Johanna',
    image: 'assets/images/johanna_b.jpg',
    widgetLink: 'https://widget.calenso.com/?partner=calenso-meeting&isFrame=true&lang=de_CH&utm_source=outreach&utm_medium=email&type=appointment&store_id=15166&category[]=11978&service[]=42819&worker_id=82220&lang-switcher=false',
  },
  marvin: {
    name: 'Marvin',
    image: 'assets/images/marvin.f.webp',
    widgetLink: 'https://widget.calenso.com/?partner=calenso-meeting&isFrame=true&lang=de_CH&utm_source=outreach&utm_medium=email&type=appointment&store_id=15166&worker_id=52775&lang-switcher=false',
  }
};

export { BANNER, BOOK_DEMO, FEATURES, COMPANIES, REFERENCE_STORIES, STATISTICS, USPS };
